import { useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Lock, Mail, ArrowRight } from "lucide-react";

/**
 * Placeholder rendered for any page set to status="coming_soon" by the admin.
 * Reuses the page's configured title + body and (optionally) captures emails
 * into the existing newsletter list via /api/page-visibility/:key/notify.
 */
export default function ComingSoon({ pageKey, title, body, notifyEnabled = true }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Please enter your email");
    setSubmitting(true);
    try {
      await api.post(`/page-visibility/${pageKey}/notify`, {
        email: email.trim().toLowerCase(),
        full_name: name.trim(),
      });
      setDone(true);
      toast.success("You're on the list — we'll email you the moment it goes live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App relative min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-32" data-testid={`coming-soon-${pageKey}`}>
        <div className="border border-[#27272A] bg-[#0F0F0F] p-8 md:p-14 relative overflow-hidden">
          {/* Decorative grain */}
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#FF331F]/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-[#FF331F] text-white text-[10px] uppercase tracking-[0.3em] font-bold px-3 py-1.5 mb-6">
              <Lock className="h-3 w-3" /> Coming Soon
            </div>
            <h1
              className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mb-6"
              data-testid="coming-soon-title"
            >
              {title}
            </h1>
            <p
              className="text-zinc-300 text-base md:text-lg leading-relaxed mb-10 whitespace-pre-wrap"
              data-testid="coming-soon-body"
            >
              {body}
            </p>

            {notifyEnabled && !done && (
              <form onSubmit={submit} className="space-y-3 border-t border-[#27272A] pt-8" data-testid="coming-soon-notify-form">
                <div className="text-[#FF331F] text-[10px] uppercase tracking-[0.3em] font-bold flex items-center gap-2">
                  <Mail className="h-3 w-3" /> Notify me when it&apos;s live
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <Input
                    placeholder="Your name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-[#050505] border-[#27272A] rounded-sm"
                    data-testid="coming-soon-name-input"
                  />
                  <Input
                    required
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-[#050505] border-[#27272A] rounded-sm"
                    data-testid="coming-soon-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm"
                  data-testid="coming-soon-notify-submit"
                >
                  {submitting ? "Adding…" : (<>Notify me <ArrowRight className="ml-2 h-3 w-3" /></>)}
                </Button>
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  We&apos;ll only email you about this feature. No spam — promise.
                </p>
              </form>
            )}

            {done && (
              <div className="border-t border-[#27272A] pt-8" data-testid="coming-soon-thanks">
                <div className="text-emerald-400 text-[10px] uppercase tracking-[0.3em] font-bold mb-2">You&apos;re in</div>
                <p className="text-zinc-300">
                  Thanks — we&apos;ll send you a heads up the moment this drops.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
